import { renderProductOption } from "@/features/product/components/ProductAutocompleteOption";
import { useSubmitGuard } from "@/hooks/useSubmitGuard";
import {
	type AutocompleteFetcher,
	type AutocompleteOption,
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	FormBuilder,
} from "@k3-tech/react-kit";

export interface UnavailableItemFormValues {
	branchID: string;
	productID: string;
	note?: string;
}

export interface UnavailableItemDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	branchFetcher: AutocompleteFetcher;
	productFetcher: AutocompleteFetcher;
	defaultBranchOption: AutocompleteOption | null;
	// Set when the dialog is opened from a product row: the product is fixed.
	lockedProductOption?: AutocompleteOption | null;
	onSubmit: (values: UnavailableItemFormValues) => unknown;
}

export function UnavailableItemDialog({
	open,
	onOpenChange,
	branchFetcher,
	productFetcher,
	defaultBranchOption,
	lockedProductOption,
	onSubmit,
}: UnavailableItemDialogProps) {
	// A disabled field submits no value, so the locked product is merged back in.
	const { isSubmitting, guardedSubmit } = useSubmitGuard(
		(values: UnavailableItemFormValues) =>
			onSubmit(
				lockedProductOption
					? { ...values, productID: String(lockedProductOption.value) }
					: values,
			),
	);
	const selectedBranchOptions = defaultBranchOption
		? [defaultBranchOption]
		: [];

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogContent className="max-w-md">
				<DialogHeader>
					<DialogTitle>Mark Unavailable</DialogTitle>
				</DialogHeader>
				<FormBuilder<UnavailableItemFormValues>
					key={`${defaultBranchOption?.value ?? "no-branch"}-${
						lockedProductOption?.value ?? "no-product"
					}`}
					onSubmit={guardedSubmit}
					isSubmitting={isSubmitting}
					onCancel={() => onOpenChange(false)}
					actionsClassName="justify-end"
					showActionsSeparator={false}
					sections={[
						{
							layout: "grid",
							grid: { cols: 1, gap: "gap-4" },
							fields: [
								{
									name: "branchID",
									label: "Branch",
									type: "autocomplete",
									options: [],
									autocompleteMode: "server",
									fetcher: branchFetcher,
									placeholder: "Search branch",
									required: true,
									// Server-mode autocomplete renders the raw UUID unless the
									// preselected option is supplied to both props.
									initialSelectedOptions: selectedBranchOptions,
									loadSelected: () => Promise.resolve(selectedBranchOptions),
									defaultValue: defaultBranchOption?.value ?? null,
								},
								{
									name: "productID",
									label: "Product",
									type: "autocomplete",
									options: [],
									autocompleteMode: "server",
									fetcher: productFetcher,
									renderOption: renderProductOption,
									placeholder: "Search product",
									required: true,
									disabled: !!lockedProductOption,
									initialSelectedOptions: lockedProductOption
										? [lockedProductOption]
										: undefined,
									loadSelected: lockedProductOption
										? () => Promise.resolve([lockedProductOption])
										: undefined,
									defaultValue: lockedProductOption?.value ?? null,
								},
								{
									name: "note",
									label: "Note",
									type: "text",
									placeholder: "Optional",
									defaultValue: "",
								},
							],
						},
					]}
					submitLabel="Save"
				/>
			</DialogContent>
		</Dialog>
	);
}
